import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Tabs, Tab, Card, Typography, Button, IconButton, TextField,
  MenuItem, Select, FormControl, InputLabel, Switch, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert, Chip, Tooltip, Pagination,
  Avatar, ThemeProvider, createTheme,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, DeleteOutline as DeleteIcon, Save as SaveIcon,
  ArrowUpward, ArrowDownward, Search as SearchIcon, Refresh as RefreshIcon,
  Close as CloseIcon, EmojiEvents, VerticalAlignTop, VerticalAlignBottom,
  Settings as SettingsIcon, Tune as TuneIcon, Category as CategoryIcon,
  Storefront as StorefrontIcon, ViewModule as ViewModuleIcon,
  PowerSettingsNew,
} from '@mui/icons-material';
import { apiFetch, resolveImageUrl } from '../utils/api';

/* ───────────────────────── theme (scoped to this page) ───────────────────────── */
const C = {
  bg: '#0e1525',
  card: '#151c2c',
  panel: '#101725',
  border: 'rgba(255,255,255,0.07)',
  borderStrong: 'rgba(255,255,255,0.12)',
  blue: '#4f8cff',
  green: '#2ee6a6',
  red: '#f0556c',
  text: '#e7eaf2',
  muted: '#8b93a7',
  head: '#6b7488',
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: C.blue },
    success: { main: C.green },
    error: { main: C.red },
    background: { default: C.bg, paper: C.card },
    text: { primary: C.text, secondary: C.muted },
    divider: C.border,
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: 'inherit',
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCard: { styleOverrides: { root: { backgroundImage: 'none', border: `1px solid ${C.border}`, backgroundColor: C.card } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiButton: { styleOverrides: { root: { borderRadius: 10 } } },
    MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 44, color: C.muted } } },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: C.border, fontSize: 13.5 },
        head: { color: C.head, fontWeight: 700, fontSize: 11, letterSpacing: 0.7, textTransform: 'uppercase' },
      },
    },
    MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 10, backgroundColor: C.panel } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
  },
});

/* ───────────────────────── types ───────────────────────── */
interface Game {
  id: number; game_uid: string; game_name: string | null; vendor: string | null;
  game_type: string | null; game_icon: string | null; static_game_icon: string | null;
  description: string | null; is_active: boolean;
}
interface Section {
  id: number; section_key: string; title: string; game_ids: number[];
  games: Game[]; is_active: boolean; sort_order: number;
}
interface Pagination { current_page: number; per_page: number; total: number; total_pages: number; }

const gameIcon = (g: Game) => resolveImageUrl(g.static_game_icon || g.game_icon || '');
const shortUid = (u: string) => (u && u.length > 10 ? `${u.slice(0, 8)}…` : u);
type MoveTo = 'top' | 'up' | 'down' | 'bottom';
function moveItem<T>(arr: T[], idx: number, to: MoveTo): T[] {
  const next = [...arr];
  const [item] = next.splice(idx, 1);
  if (to === 'top') next.unshift(item);
  else if (to === 'bottom') next.push(item);
  else if (to === 'up') next.splice(Math.max(0, idx - 1), 0, item);
  else next.splice(Math.min(next.length, idx + 1), 0, item);
  return next;
}

/* ───────────────────────── small UI pieces ───────────────────────── */
const PageHeader: React.FC<{ title: string; subtitle: React.ReactNode; icon: React.ReactNode }> = ({ title, subtitle, icon }) => (
  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
    <Box sx={{
      width: 52, height: 52, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: 'rgba(79,140,255,0.14)', color: C.blue,
    }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="h5" fontWeight={800} sx={{ color: C.text, lineHeight: 1.1 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: C.muted, mt: 0.5 }}>
        {subtitle}
      </Typography>
    </Box>
  </Stack>
);

// Toolbar row above a priority table: "N games in X" + Add / Reload / Save.
const PriorityToolbar: React.FC<{
  count: number; label: string; dirty?: boolean; saving?: boolean;
  onAdd: () => void; onReload?: () => void; onSave?: () => void;
  rightExtra?: React.ReactNode;
}> = ({ count, label, dirty, saving, onAdd, onReload, onSave, rightExtra }) => (
  <Card sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
    <Typography sx={{ color: C.muted }}>
      <b style={{ color: C.text }}>{count}</b> game{count === 1 ? '' : 's'} in <b style={{ color: C.text }}>{label}</b>
    </Typography>
    <Box sx={{ flex: 1 }} />
    {rightExtra}
    <Button variant="outlined" color="success" startIcon={<AddIcon />} onClick={onAdd}
      sx={{ borderColor: 'rgba(46,230,166,0.5)' }}>Add Games</Button>
    {onReload && <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onReload}
      sx={{ borderColor: C.borderStrong, color: C.muted }}>Reload</Button>}
    {onSave && (
      <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        disabled={!dirty || saving} onClick={onSave}>Save Priority</Button>
    )}
  </Card>
);

// The reorderable priority table (presentational).
const PriorityTable: React.FC<{
  list: Game[];
  onMove: (idx: number, to: MoveTo) => void;
  onRemove: (id: number) => void;
  onEdit?: (g: Game) => void;
  emptyHint?: string;
}> = ({ list, onMove, onRemove, onEdit, emptyHint }) => (
  <Card>
    <TableContainer sx={{ maxHeight: 560 }}>
      <Table size="medium" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell width={56}>#</TableCell>
            <TableCell>Game</TableCell>
            <TableCell>Provider</TableCell>
            <TableCell align="center" width={260}>Priority</TableCell>
            <TableCell align="right" width={110}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((g, idx) => (
            <TableRow key={g.id} hover sx={{ opacity: g.is_active ? 1 : 0.5 }}>
              <TableCell sx={{ color: C.muted }}>{idx + 1}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar variant="rounded" src={gameIcon(g)} sx={{ width: 40, height: 40, bgcolor: C.panel, borderRadius: 1.5 }}>
                    {(g.game_name || '?').charAt(0)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={700} sx={{ color: C.text }} noWrap>{g.game_name || g.game_uid}</Typography>
                    <Typography variant="caption" sx={{ color: C.muted, fontFamily: 'monospace' }}>{shortUid(g.game_uid)}</Typography>
                  </Box>
                  {!g.is_active && <Chip size="small" label="inactive" color="warning" variant="outlined" />}
                </Stack>
              </TableCell>
              <TableCell sx={{ color: C.muted }}>{g.vendor}</TableCell>
              <TableCell align="center">
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                  <Button size="small" disabled={idx === 0} onClick={() => onMove(idx, 'top')}
                    sx={{ minWidth: 0, color: C.muted, fontSize: 12 }} startIcon={<VerticalAlignTop sx={{ fontSize: 16 }} />}>Top</Button>
                  <IconButton size="small" disabled={idx === 0} onClick={() => onMove(idx, 'up')}><ArrowUpward fontSize="small" /></IconButton>
                  <IconButton size="small" disabled={idx === list.length - 1} onClick={() => onMove(idx, 'down')}><ArrowDownward fontSize="small" /></IconButton>
                  <Button size="small" disabled={idx === list.length - 1} onClick={() => onMove(idx, 'bottom')}
                    sx={{ minWidth: 0, color: C.muted, fontSize: 12 }} startIcon={<VerticalAlignBottom sx={{ fontSize: 16 }} />}>Bottom</Button>
                </Stack>
              </TableCell>
              <TableCell align="right">
                {onEdit && (
                  <Tooltip title="Edit game">
                    <IconButton size="small" onClick={() => onEdit(g)}
                      sx={{ border: `1px solid ${C.borderStrong}`, mr: 0.5, color: C.blue }}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Remove from list">
                  <IconButton size="small" onClick={() => onRemove(g.id)}
                    sx={{ border: '1px solid rgba(240,85,108,0.4)', color: C.red }}><DeleteIcon fontSize="small" /></IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {!list.length && (
            <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: C.muted }}>
              {emptyHint || 'No games yet. Click "Add Games" to start.'}
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
);

/* ============================================================
 *  Sections (rails as sub-tabs, like the reference)
 * ========================================================== */
const SectionsTab: React.FC = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState<Game[]>([]);            // working copy for active section
  const [picker, setPicker] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsFor, setSettingsFor] = useState<Section | null>(null);
  const [editing, setEditing] = useState<Game | null>(null);

  const current = sections[active];

  const load = async (keepActive = true) => {
    try {
      setLoading(true); setError(null);
      const res = await apiFetch<{ data: Section[] }>('/jsGames/admin/sections');
      const data = res.data || [];
      setSections(data);
      const idx = keepActive ? Math.min(active, Math.max(0, data.length - 1)) : 0;
      setActive(idx);
      setList(data[idx]?.games || []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(false); /* eslint-disable-next-line */ }, []);
  useEffect(() => { setList(sections[active]?.games || []); /* eslint-disable-next-line */ }, [active]);

  const dirty = useMemo(
    () => current && list.map(g => g.id).join(',') !== current.games.map(g => g.id).join(','),
    [list, current]
  );

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const res = await apiFetch<{ data: Section }>(`/jsGames/admin/sections/${current.id}`, {
        method: 'PUT', body: JSON.stringify({ game_ids: list.map(g => g.id) }),
      });
      setSections(prev => prev.map((s, i) => i === active ? res.data : s));
      setList(res.data.games);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {/* rail sub-tabs */}
      <Card sx={{ px: 1, mb: 2, display: 'flex', alignItems: 'center' }}>
        <Tabs value={active} onChange={(_, v) => setActive(v)} variant="scrollable" scrollButtons="auto"
          sx={{ flex: 1, minHeight: 48, '& .Mui-selected': { color: `${C.blue} !important` } }}>
          {sections.map(s => (
            <Tab key={s.id} sx={{ gap: 1 }} label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{s.title}</span>
                <Chip size="small" label={s.games.length}
                  sx={{ height: 18, fontSize: 11, bgcolor: C.panel, color: C.muted }} />
                {!s.is_active && <Chip size="small" label="off" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
              </Stack>
            } />
          ))}
        </Tabs>
        <Button startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} sx={{ ml: 1, whiteSpace: 'nowrap' }}>New Rail</Button>
      </Card>

      {current && (
        <>
          <PriorityToolbar
            count={list.length} label={current.title} dirty={dirty} saving={saving}
            onAdd={() => setPicker(true)} onReload={() => load(true)} onSave={save}
            rightExtra={
              <Tooltip title="Rail settings">
                <IconButton onClick={() => setSettingsFor(current)} sx={{ border: `1px solid ${C.borderStrong}`, color: C.muted }}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
          <PriorityTable
            list={list}
            onMove={(idx, to) => setList(l => moveItem(l, idx, to))}
            onRemove={(id) => setList(l => l.filter(g => g.id !== id))}
            onEdit={setEditing}
            emptyHint={`No games in "${current.title}" yet. Click "Add Games".`}
          />
        </>
      )}
      {!sections.length && <Typography sx={{ color: C.muted }}>No rails yet. Click "New Rail" to begin.</Typography>}

      {picker && current && (
        <GamePickerDialog excludeIds={list.map(g => g.id)} onClose={() => setPicker(false)}
          onConfirm={(games) => { const have = new Set(list.map(g => g.id)); setList([...list, ...games.filter(g => !have.has(g.id))]); setPicker(false); }} />
      )}
      {createOpen && (
        <CreateSectionDialog existingOrders={sections.map(s => s.sort_order)} onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(false); }} onError={setError} />
      )}
      {settingsFor && (
        <SectionSettingsDialog section={settingsFor} onClose={() => setSettingsFor(null)}
          onSaved={() => { setSettingsFor(null); load(true); }}
          onDeleted={() => { setSettingsFor(null); load(false); }} onError={setError} />
      )}
      {editing && <EditGameDialog game={editing} onClose={() => setEditing(null)}
        onSaved={(g) => { setList(l => l.map(x => x.id === g.id ? g : x)); setEditing(null); }} onError={setError} />}
    </Box>
  );
};

const CreateSectionDialog: React.FC<{
  existingOrders: number[]; onClose: () => void; onCreated: () => void; onError: (m: string) => void;
}> = ({ existingOrders, onClose, onCreated, onError }) => {
  const [title, setTitle] = useState('');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const autoKey = title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const submit = async () => {
    try {
      setBusy(true);
      const sort_order = (Math.max(0, ...existingOrders) || 0) + 1;
      await apiFetch('/jsGames/admin/sections', { method: 'POST', body: JSON.stringify({ section_key: key || autoKey, title, sort_order }) });
      onCreated();
    } catch (e: any) { onError(e.message); } finally { setBusy(false); }
  };
  return (
    <ThemeProvider theme={theme}>
      <Dialog open onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { bgcolor: C.card, backgroundImage: 'none' } }}>
        <DialogTitle fontWeight={700}>New Rail</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField label="Title" value={title} onChange={e => setTitle(e.target.value)} autoFocus size="small" />
          <TextField label="Key (storefront id)" value={key} onChange={e => setKey(e.target.value)} size="small"
            placeholder={autoKey || 'e.g. hot_games'} helperText="Blank = auto-generate from title" />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} color="inherit">Cancel</Button>
          <Button variant="contained" disabled={!title.trim() || busy} onClick={submit}>{busy ? <CircularProgress size={20} /> : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

const SectionSettingsDialog: React.FC<{
  section: Section; onClose: () => void; onSaved: () => void; onDeleted: () => void; onError: (m: string) => void;
}> = ({ section, onClose, onSaved, onDeleted, onError }) => {
  const [title, setTitle] = useState(section.title);
  const [order, setOrder] = useState(section.sort_order);
  const [activeFlag, setActiveFlag] = useState(section.is_active);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    try {
      setBusy(true);
      await apiFetch(`/jsGames/admin/sections/${section.id}`, {
        method: 'PUT', body: JSON.stringify({ title, sort_order: order, is_active: activeFlag }),
      });
      onSaved();
    } catch (e: any) { onError(e.message); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete rail "${section.title}"? Games are not deleted, only the rail.`)) return;
    try { await apiFetch(`/jsGames/admin/sections/${section.id}`, { method: 'DELETE' }); onDeleted(); }
    catch (e: any) { onError(e.message); }
  };
  return (
    <ThemeProvider theme={theme}>
      <Dialog open onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { bgcolor: C.card, backgroundImage: 'none' } }}>
        <DialogTitle fontWeight={700}>Rail settings · <Chip size="small" label={section.section_key} sx={{ bgcolor: C.panel }} /></DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField label="Title" value={title} onChange={e => setTitle(e.target.value)} size="small" />
          <TextField label="Sort order" type="number" value={order} onChange={e => setOrder(parseInt(e.target.value) || 0)} size="small" />
          <Stack direction="row" alignItems="center" justifyContent="space-between"
            sx={{ border: `1px solid ${C.border}`, borderRadius: 2, px: 2, py: 1 }}>
            <Typography variant="body2">Visible on storefront</Typography>
            <Switch checked={activeFlag} onChange={e => setActiveFlag(e.target.checked)} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button color="error" startIcon={<DeleteIcon />} onClick={del}>Delete rail</Button>
          <Box>
            <Button onClick={onClose} color="inherit">Cancel</Button>
            <Button variant="contained" disabled={busy} onClick={save} sx={{ ml: 1 }}>{busy ? <CircularProgress size={20} /> : 'Save'}</Button>
          </Box>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

/* ============================================================
 *  Shared — Game picker dialog
 * ========================================================== */
const GamePickerDialog: React.FC<{
  excludeIds: number[]; onClose: () => void; onConfirm: (games: Game[]) => void;
  initialType?: string; initialVendor?: string;
}> = ({ excludeIds, onClose, onConfirm, initialType, initialVendor }) => {
  const [keyword, setKeyword] = useState('');
  const [type, setType] = useState(initialType || '');
  const [vendor, setVendor] = useState(initialVendor || '');
  const [games, setGames] = useState<Game[]>([]);
  const [page, setPage] = useState(1);
  const [pag, setPag] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Map<number, Game>>(new Map());

  const search = async (p = 1) => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ page: String(p), per_page: '20' });
      if (keyword) q.set('keyword', keyword);
      if (type) q.set('type', type);
      if (vendor) q.set('vendor', vendor);
      const res = await apiFetch<{ data: { games: Game[]; pagination: Pagination } }>(`/jsGames/admin/games?${q}`);
      setGames(res.data.games); setPag(res.data.pagination); setPage(p);
    } finally { setLoading(false); }
  };
  useEffect(() => { search(1); /* eslint-disable-next-line */ }, []);

  const toggle = (g: Game) => setSelected(prev => {
    const next = new Map(prev); next.has(g.id) ? next.delete(g.id) : next.set(g.id, g); return next;
  });

  return (
    <ThemeProvider theme={theme}>
      <Dialog open onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { bgcolor: C.card, backgroundImage: 'none' } }}>
        <DialogTitle fontWeight={700}>Add games {selected.size ? <Chip size="small" color="success" label={`${selected.size} selected`} sx={{ ml: 1 }} /> : ''}</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <TextField size="small" label="Search name / vendor / uid" value={keyword}
              onChange={e => setKeyword(e.target.value)} onKeyDown={e => e.key === 'Enter' && search(1)} />
            <TextField size="small" label="Type" value={type} onChange={e => setType(e.target.value)} onKeyDown={e => e.key === 'Enter' && search(1)} />
            <TextField size="small" label="Vendor" value={vendor} onChange={e => setVendor(e.target.value)} onKeyDown={e => e.key === 'Enter' && search(1)} />
            <Button variant="contained" startIcon={<SearchIcon />} onClick={() => search(1)}>Search</Button>
          </Stack>
          {loading ? <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box> : (
            <TableContainer component={Card} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  <TableCell width={40} /><TableCell>Game</TableCell><TableCell>Vendor</TableCell><TableCell>Type</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {games.map(g => {
                    const already = excludeIds.includes(g.id);
                    return (
                      <TableRow key={g.id} hover selected={selected.has(g.id)}
                        sx={{ cursor: already ? 'default' : 'pointer', opacity: already ? 0.4 : 1 }}
                        onClick={() => !already && toggle(g)}>
                        <TableCell padding="checkbox"><input type="checkbox" readOnly checked={already || selected.has(g.id)} disabled={already} /></TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar variant="rounded" src={gameIcon(g)} sx={{ width: 30, height: 30, bgcolor: C.panel }}>{(g.game_name || '?').charAt(0)}</Avatar>
                            {g.game_name || g.game_uid}{already && <Chip size="small" label="in list" sx={{ ml: 1, bgcolor: C.panel }} />}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ color: C.muted }}>{g.vendor}</TableCell>
                        <TableCell sx={{ color: C.muted }}>{g.game_type}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {pag && pag.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={pag.total_pages} page={page} onChange={(_, p) => search(p)} size="small" color="primary" />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} color="inherit">Cancel</Button>
          <Button variant="contained" color="success" disabled={!selected.size} onClick={() => onConfirm([...selected.values()])}>
            Add {selected.size || ''}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

/* ============================================================
 *  Shared — Edit game dialog
 * ========================================================== */
const EditGameDialog: React.FC<{
  game: Game; onClose: () => void; onSaved: (g: Game) => void; onError: (m: string) => void;
}> = ({ game, onClose, onSaved, onError }) => {
  const [form, setForm] = useState({
    game_name: game.game_name || '', vendor: game.vendor || '', game_type: game.game_type || '',
    game_icon: game.game_icon || '', static_game_icon: game.static_game_icon || '',
    description: game.description || '', is_active: game.is_active,
  });
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: any) => setForm(prev => ({ ...prev, [k]: v }));
  const preview = resolveImageUrl(form.static_game_icon || form.game_icon);
  const submit = async () => {
    try {
      setBusy(true);
      const res = await apiFetch<{ data: Game }>(`/jsGames/admin/games/${game.id}`, { method: 'PUT', body: JSON.stringify(form) });
      onSaved(res.data);
    } catch (e: any) { onError(e.message); } finally { setBusy(false); }
  };
  return (
    <ThemeProvider theme={theme}>
      <Dialog open onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { bgcolor: C.card, backgroundImage: 'none' } }}>
        <DialogTitle fontWeight={700}>Edit game #{game.id} <Typography component="span" variant="caption" sx={{ color: C.muted }}>({game.game_uid})</Typography></DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar variant="rounded" src={preview} sx={{ width: 64, height: 64, bgcolor: C.panel }}>{(form.game_name || '?').charAt(0)}</Avatar>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
              <Typography variant="body2" sx={{ color: C.muted }}>{form.is_active ? 'Active' : 'Inactive'}</Typography>
              <Switch checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
            </Stack>
          </Stack>
          <TextField label="Game name" value={form.game_name} onChange={e => set('game_name', e.target.value)} size="small" />
          <Stack direction="row" spacing={2}>
            <TextField label="Vendor" value={form.vendor} onChange={e => set('vendor', e.target.value)} fullWidth size="small" />
            <TextField label="Type" value={form.game_type} onChange={e => set('game_type', e.target.value)} fullWidth size="small" />
          </Stack>
          <TextField label="Game icon URL" value={form.game_icon} onChange={e => set('game_icon', e.target.value)} size="small" />
          <TextField label="Static icon URL" value={form.static_game_icon} onChange={e => set('static_game_icon', e.target.value)} size="small" />
          <TextField label="Description" value={form.description} onChange={e => set('description', e.target.value)} multiline minRows={2} size="small" />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} color="inherit">Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} disabled={busy} onClick={submit}>{busy ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

/* ============================================================
 *  Shared — selector + priority editor (Vendor / Type tabs)
 * ========================================================== */
const SelectorPriority: React.FC<{
  selectLabel: string;
  options: { value: string; label: string }[];
  loadPath: (v: string) => string;
  emptyHint: string;
  pickerKey: 'vendor' | 'type';
}> = ({ selectLabel, options, loadPath, emptyHint, pickerKey }) => {
  const [value, setValue] = useState('');
  const [list, setList] = useState<Game[]>([]);
  const [base, setBase] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(false);
  const [editing, setEditing] = useState<Game | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async (v: string) => {
    setValue(v); setList([]); setBase([]);
    if (!v) return;
    setLoading(true);
    try {
      const r = await apiFetch<{ data: { games: Game[] } }>(loadPath(v));
      setList(r.data.games); setBase(r.data.games);
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  };
  const dirty = list.map(g => g.id).join(',') !== base.map(g => g.id).join(',');
  const save = async () => {
    setSaving(true);
    try {
      const r = await apiFetch<{ data: { games: Game[] } }>(loadPath(value), {
        method: 'PUT', body: JSON.stringify({ game_ids: list.map(g => g.id) }),
      });
      setList(r.data.games); setBase(r.data.games);
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      <Card sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 340 }}>
          <InputLabel>{selectLabel}</InputLabel>
          <Select label={selectLabel} value={value} onChange={e => load(e.target.value)}
            MenuProps={{ PaperProps: { sx: { bgcolor: C.card, maxHeight: 400 } } }}>
            {options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        {loading && <CircularProgress size={22} />}
        <Typography variant="body2" sx={{ color: C.muted }}>Pinned games sort to the top of this list in the lobby.</Typography>
      </Card>

      {value && !loading && (
        <>
          <PriorityToolbar count={list.length} label={options.find(o => o.value === value)?.label || value}
            dirty={dirty} saving={saving} onAdd={() => setPicker(true)} onReload={() => load(value)} onSave={save} />
          <PriorityTable list={list}
            onMove={(idx, to) => setList(l => moveItem(l, idx, to))}
            onRemove={(id) => setList(l => l.filter(g => g.id !== id))}
            onEdit={setEditing} emptyHint={`No pinned games yet. Click "Add Games".`} />
        </>
      )}
      {!value && <Typography sx={{ color: C.muted }}>{emptyHint}</Typography>}

      {picker && (
        <GamePickerDialog excludeIds={list.map(g => g.id)}
          initialVendor={pickerKey === 'vendor' ? value : undefined}
          initialType={pickerKey === 'type' ? value : undefined}
          onClose={() => setPicker(false)}
          onConfirm={(games) => { const have = new Set(list.map(g => g.id)); setList([...list, ...games.filter(g => !have.has(g.id))]); setPicker(false); }} />
      )}
      {editing && <EditGameDialog game={editing} onClose={() => setEditing(null)}
        onSaved={(g) => { setList(l => l.map(x => x.id === g.id ? g : x)); setEditing(null); }} onError={setError} />}
    </Box>
  );
};

const VendorPriorityTab: React.FC = () => {
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    apiFetch<{ data: { vendor: string; count: number }[] }>('/jsGames/admin/vendors')
      .then(r => setOptions((r.data || []).map(v => ({ value: v.vendor, label: `${v.vendor} (${v.count})` })))).catch(() => {});
  }, []);
  return <SelectorPriority selectLabel="Vendor" options={options} pickerKey="vendor"
    loadPath={(v) => `/jsGames/admin/vendor-priority/${encodeURIComponent(v)}`}
    emptyHint="Pick a vendor to manage its pinned games." />;
};

const TypePriorityTab: React.FC = () => {
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    apiFetch<{ data: { game_type: string; count: number }[] }>('/jsGames/admin/game-types')
      .then(r => setOptions((r.data || []).map(t => ({ value: t.game_type, label: `${t.game_type} (${t.count})` })))).catch(() => {});
  }, []);
  return <SelectorPriority selectLabel="Game type" options={options} pickerKey="type"
    loadPath={(t) => `/jsGames/admin/type-priority/${encodeURIComponent(t)}`}
    emptyHint="Pick a game type to manage its pinned games." />;
};

/* ============================================================
 *  Providers On/Off
 * ========================================================== */
const ProvidersTab: React.FC = () => {
  const [providers, setProviders] = useState<{ vendor: string; game_count: number; enabled: boolean }[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try { const r = await apiFetch<{ data: typeof providers }>('/jsGames/admin/providers'); setProviders(r.data || []); }
    catch (e: any) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (vendor: string, enabled: boolean) => {
    setBusy(vendor);
    try {
      await apiFetch(`/jsGames/admin/providers/${encodeURIComponent(vendor)}`, { method: 'PUT', body: JSON.stringify({ enabled }) });
      setProviders(prev => prev.map(p => p.vendor === vendor ? { ...p, enabled } : p));
    } catch (e: any) { setError(e.message); } finally { setBusy(null); }
  };

  const shown = providers.filter(p => p.vendor.toLowerCase().includes(filter.toLowerCase()));
  const onCount = providers.filter(p => p.enabled).length;
  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      <Card sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField size="small" label="Filter vendor" value={filter} onChange={e => setFilter(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: C.muted }} /> }} />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} sx={{ borderColor: C.borderStrong, color: C.muted }}>Reload</Button>
        <Box sx={{ flex: 1 }} />
        <Chip label={`${onCount} enabled`} color="success" variant="outlined" />
        <Chip label={`${providers.length - onCount} disabled`} sx={{ bgcolor: C.panel, color: C.muted }} />
      </Card>
      <Card>
        <TableContainer sx={{ maxHeight: 620 }}>
          <Table stickyHeader>
            <TableHead><TableRow>
              <TableCell>Vendor</TableCell><TableCell align="right">Games</TableCell><TableCell align="right" width={160}>Status</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {shown.map(p => (
                <TableRow key={p.vendor} hover>
                  <TableCell><Typography fontWeight={600}>{p.vendor}</Typography></TableCell>
                  <TableCell align="right" sx={{ color: C.muted }}>{p.game_count}</TableCell>
                  <TableCell align="right">
                    {busy === p.vendor ? <CircularProgress size={18} /> : (
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                        <Chip size="small" label={p.enabled ? 'Enabled' : 'Disabled'}
                          color={p.enabled ? 'success' : 'default'} variant={p.enabled ? 'filled' : 'outlined'}
                          sx={!p.enabled ? { color: C.muted } : undefined} />
                        <Switch checked={p.enabled} onChange={e => toggle(p.vendor, e.target.checked)} />
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!shown.length && <TableRow><TableCell colSpan={3} align="center" sx={{ py: 5, color: C.muted }}>No providers.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

/* ============================================================
 *  Type-wise games (grid)
 * ========================================================== */
const TypeWiseTab: React.FC = () => {
  const [types, setTypes] = useState<{ game_type: string; count: number }[]>([]);
  const [type, setType] = useState('');
  const [games, setGames] = useState<Game[]>([]);
  const [pag, setPag] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState<Section[]>([]);
  const [addTarget, setAddTarget] = useState<Game | null>(null);

  useEffect(() => {
    apiFetch<{ data: { game_type: string; count: number }[] }>('/jsGames/admin/game-types').then(r => setTypes(r.data || [])).catch(() => {});
    apiFetch<{ data: Section[] }>('/jsGames/admin/sections').then(r => setSections(r.data || [])).catch(() => {});
  }, []);

  const load = async (p = 1, t = type) => {
    if (!t) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ type: t, page: String(p), per_page: '24' });
      const res = await apiFetch<{ data: { games: Game[]; pagination: Pagination } }>(`/jsGames/admin/games?${q}`);
      setGames(res.data.games); setPag(res.data.pagination); setPage(p);
    } finally { setLoading(false); }
  };

  const addToSection = async (sectionId: number, game: Game) => {
    const s = sections.find(x => x.id === sectionId); if (!s) return;
    const ids = s.game_ids.includes(game.id) ? s.game_ids : [...s.game_ids, game.id];
    const res = await apiFetch<{ data: Section }>(`/jsGames/admin/sections/${sectionId}`, { method: 'PUT', body: JSON.stringify({ game_ids: ids }) });
    setSections(prev => prev.map(x => x.id === sectionId ? res.data : x));
    setAddTarget(null);
  };

  return (
    <Box>
      <Card sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 320 }}>
          <InputLabel>Game type</InputLabel>
          <Select label="Game type" value={type} onChange={e => { setType(e.target.value); load(1, e.target.value); }}
            MenuProps={{ PaperProps: { sx: { bgcolor: C.card, maxHeight: 400 } } }}>
            {types.map(t => <MenuItem key={t.game_type} value={t.game_type}>{t.game_type} ({t.count})</MenuItem>)}
          </Select>
        </FormControl>
        {loading && <CircularProgress size={22} />}
      </Card>

      {!type && <Typography sx={{ color: C.muted }}>Pick a type to browse its games.</Typography>}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 2 }}>
        {games.map(g => (
          <Card key={g.id} sx={{ overflow: 'hidden', opacity: g.is_active ? 1 : 0.55, transition: 'transform .15s', '&:hover': { transform: 'translateY(-3px)', borderColor: C.borderStrong } }}>
            <Box sx={{ height: 118, bgcolor: C.panel, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {gameIcon(g) ? <img src={gameIcon(g)} alt="" style={{ maxHeight: '100%', maxWidth: '100%' }} /> : <Typography variant="caption" sx={{ color: C.muted }}>no icon</Typography>}
            </Box>
            <Box sx={{ p: 1.5 }}>
              <Typography variant="body2" fontWeight={700} noWrap>{g.game_name || g.game_uid}</Typography>
              <Typography variant="caption" sx={{ color: C.muted }} noWrap display="block">{g.vendor}</Typography>
              <Button size="small" fullWidth startIcon={<AddIcon />} variant="outlined" color="success" sx={{ mt: 1, borderColor: 'rgba(46,230,166,0.4)' }}
                onClick={() => setAddTarget(g)}>Add to rail</Button>
            </Box>
          </Card>
        ))}
      </Box>

      {pag && pag.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={pag.total_pages} page={page} onChange={(_, p) => load(p)} color="primary" />
        </Box>
      )}

      {addTarget && (
        <ThemeProvider theme={theme}>
          <Dialog open onClose={() => setAddTarget(null)} fullWidth maxWidth="xs" PaperProps={{ sx: { bgcolor: C.card, backgroundImage: 'none' } }}>
            <DialogTitle fontWeight={700}>Add "{addTarget.game_name}" to rail</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: '8px !important' }}>
              {sections.map(s => (
                <Button key={s.id} variant="outlined" disabled={s.game_ids.includes(addTarget.id)}
                  sx={{ justifyContent: 'space-between', borderColor: C.borderStrong, color: C.text }}
                  onClick={() => addToSection(s.id, addTarget)}
                  endIcon={s.game_ids.includes(addTarget.id) ? <Chip size="small" label="added" sx={{ bgcolor: C.panel }} /> : <AddIcon />}>
                  {s.title}
                </Button>
              ))}
              {!sections.length && <Typography sx={{ color: C.muted }}>No rails yet.</Typography>}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}><Button onClick={() => setAddTarget(null)} color="inherit">Close</Button></DialogActions>
          </Dialog>
        </ThemeProvider>
      )}
    </Box>
  );
};

/* ============================================================
 *  Edit games (search + table)
 * ========================================================== */
const GamesTab: React.FC = () => {
  const [keyword, setKeyword] = useState('');
  const [type, setType] = useState('');
  const [vendor, setVendor] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [games, setGames] = useState<Game[]>([]);
  const [pag, setPag] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Game | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ page: String(p), per_page: '25' });
      if (keyword) q.set('keyword', keyword);
      if (type) q.set('type', type);
      if (vendor) q.set('vendor', vendor);
      if (activeFilter) q.set('is_active', activeFilter);
      const res = await apiFetch<{ data: { games: Game[]; pagination: Pagination } }>(`/jsGames/admin/games?${q}`);
      setGames(res.data.games); setPag(res.data.pagination); setPage(p);
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, []);

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      <Card sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="Search" value={keyword} onChange={e => setKeyword(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(1)} />
        <TextField size="small" label="Type" value={type} onChange={e => setType(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(1)} />
        <TextField size="small" label="Vendor" value={vendor} onChange={e => setVendor(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(1)} />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={activeFilter} onChange={e => setActiveFilter(e.target.value)} MenuProps={{ PaperProps: { sx: { bgcolor: C.card } } }}>
            <MenuItem value="">All</MenuItem><MenuItem value="true">Active</MenuItem><MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<SearchIcon />} onClick={() => load(1)}>Search</Button>
        {loading && <CircularProgress size={22} />}
      </Card>

      <Card>
        <TableContainer>
          <Table>
            <TableHead><TableRow>
              <TableCell width={60}>ID</TableCell><TableCell>Game</TableCell><TableCell>Vendor</TableCell>
              <TableCell>Type</TableCell><TableCell align="center">Status</TableCell><TableCell align="right">Edit</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {games.map(g => (
                <TableRow key={g.id} hover>
                  <TableCell sx={{ color: C.muted }}>{g.id}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar variant="rounded" src={gameIcon(g)} sx={{ width: 36, height: 36, bgcolor: C.panel }}>{(g.game_name || '?').charAt(0)}</Avatar>
                      <Box>
                        <Typography fontWeight={600}>{g.game_name || g.game_uid}</Typography>
                        <Typography variant="caption" sx={{ color: C.muted, fontFamily: 'monospace' }}>{shortUid(g.game_uid)}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: C.muted }}>{g.vendor}</TableCell>
                  <TableCell sx={{ color: C.muted }}>{g.game_type}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={g.is_active ? 'Active' : 'Inactive'} color={g.is_active ? 'success' : 'default'}
                      variant={g.is_active ? 'filled' : 'outlined'} sx={!g.is_active ? { color: C.muted } : undefined} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setEditing(g)} sx={{ border: `1px solid ${C.borderStrong}`, color: C.blue }}><EditIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!games.length && !loading && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: C.muted }}>No games found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      {pag && pag.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={pag.total_pages} page={page} onChange={(_, p) => load(p)} color="primary" />
        </Box>
      )}
      {editing && <EditGameDialog game={editing} onClose={() => setEditing(null)}
        onSaved={(g) => { setGames(prev => prev.map(x => x.id === g.id ? g : x)); setEditing(null); }} onError={setError} />}
    </Box>
  );
};

/* ============================================================
 *  View registry + page shell
 *  Each view is mounted by its own sidebar item / route.
 * ========================================================== */
export type PriorityView = 'rails' | 'vendor' | 'type' | 'providers' | 'typewise' | 'edit';

const VIEW_META: Record<PriorityView, { title: string; subtitle: React.ReactNode; icon: React.ReactNode; Comp: React.FC }> = {
  rails: {
    title: 'Providers Priority',
    subtitle: <>Order home-page game rails — Hot Games, New Releases, Game Shows, …</>,
    icon: <EmojiEvents />, Comp: SectionsTab,
  },
  vendor: {
    title: 'Vendor Priority',
    subtitle: <>Pin games to the top of a vendor’s list in the lobby</>,
    icon: <StorefrontIcon />, Comp: VendorPriorityTab,
  },
  type: {
    title: 'Type Priority',
    subtitle: <>Pin games to the top of a game-type list in the lobby</>,
    icon: <CategoryIcon />, Comp: TypePriorityTab,
  },
  providers: {
    title: 'Provider On / Off',
    subtitle: <>Enable or disable a vendor across the game lobby</>,
    icon: <PowerSettingsNew />, Comp: ProvidersTab,
  },
  typewise: {
    title: 'Type-wise Games',
    subtitle: <>Browse games by type and add them to a rail</>,
    icon: <ViewModuleIcon />, Comp: TypeWiseTab,
  },
  edit: {
    title: 'Edit Games',
    subtitle: <>Edit game name, vendor, type, icons and active status</>,
    icon: <TuneIcon />, Comp: GamesTab,
  },
};

// Single-view page. Each sidebar route passes its own `view`.
const InnerHousePriority: React.FC<{ view?: PriorityView }> = ({ view = 'rails' }) => {
  const meta = VIEW_META[view] || VIEW_META.rails;
  const Content = meta.Comp;
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh', bgcolor: C.bg, color: C.text }}>
        <PageHeader title={meta.title} subtitle={meta.subtitle} icon={meta.icon} />
        <Content />
      </Box>
    </ThemeProvider>
  );
};

export default InnerHousePriority;
